// @avantia common-code
import { HpCustomValidatorResult } from '@avantia/form-validation';
import {
  HpAnswerValueTypes,
  createControlId,
  getIdentifierConfigDefaultValues,
  getIdentifierConfigValues
} from '@avantia/questionset-model';
import { HpPrerequisiteThisReference } from '@avantia/questionset-script-parser';
import * as dateFns from 'date-fns';
import { registerConfigurationAction } from '../engineClient/config/registerClientConfig';
import { formatDate } from '../engineClient/tools/dateTools';
import { questionIds } from '../types/questionConstants';

export const addressSummaryDateFormat = 'MMM YYYY';

let { maxGroupsAllowed } = getIdentifierConfigDefaultValues();

registerConfigurationAction(() => {
  ({ maxGroupsAllowed } = getIdentifierConfigValues());
});

export function isAddressHistoryValid(thisRef: HpPrerequisiteThisReference): HpCustomValidatorResult {
  const addressHistory = validateAddressHistory(thisRef);
  const { isComplete, errorMessage } = addressHistory;
  return { isValid: isComplete, message: `There is a gap in your address history: ${errorMessage}` };
}

export interface HpAddressHistoryValidationResult {
  gaps: { from: string; to: string }[];
  isComplete: boolean;
  errorMessage?: string;
}

export function validateAddressHistory(thisRef: HpPrerequisiteThisReference): HpAddressHistoryValidationResult {
  const {
    movedInDate: movedInDateQid,
    movedOutDate: movedOutDateQid,
    isCurrentAddress: isCurrentAddressQid
  } = questionIds;
  const dateRanges: { from: Date; to: Date }[] = [];
  for (let i = 1; i < maxGroupsAllowed; i++) {
    const movedInDateValue = getAnswer(movedInDateQid, i);
    // TODO - Check what happens if address was deleted (should be ok).
    if (!movedInDateValue) {
      break;
    }

    // The address they say they are still at has no move-out date, and runs to today. A move-out date missing for
    // any other reason is left out, so the history it should have closed still shows as a gap.
    const movedOutDateValue = getAnswer(movedOutDateQid, i);
    const isCurrentAddress = getAnswer(isCurrentAddressQid, i) === true;
    if (!movedOutDateValue && !isCurrentAddress) {
      continue;
    }

    dateRanges.push({
      from: new Date(movedInDateValue as string),
      to: movedOutDateValue ? new Date(movedOutDateValue as string) : new Date()
    });
  }

  dateRanges.sort((a, b) => a.from.getTime() - b.from.getTime());
  const gaps: { from: string; to: string }[] = [];
  const today = new Date();
  const fiveYearsAgo = getDateFiveYearsAgo();
  const firstDate = dateRanges[0] && dateRanges[0].from;

  // Check if there is a gap from 5 years ago to the first date
  if (firstDate) {
    if (firstDate > fiveYearsAgo) {
      pushToGaps({ from: fiveYearsAgo, to: firstDate });
    }
  }

  // Check if there is a gap from the last date to today
  const lastDate = dateRanges[dateRanges.length - 1] && dateRanges[dateRanges.length - 1].to;
  if (lastDate) {
    if (!areDatesContiguous(lastDate, today)) {
      pushToGaps({ from: lastDate, to: today });
    }
  }

  for (let i = 0; i < dateRanges.length; i++) {
    const nextDate = dateRanges[i + 1];
    if (!nextDate) {
      break;
    }

    if (!areDatesContiguous(dateRanges[i].to, nextDate.from)) {
      pushToGaps({ from: dateRanges[i].to, to: dateRanges[i + 1].from });
    }
  }

  if (!gaps.length) {
    return { isComplete: true, gaps };
  }

  const sortedGaps = gaps.sort((a, b) => new Date(a.from).getTime() - new Date(b.from).getTime());

  const { from, to } = gaps[0];
  const fromString = formatDate(from, addressSummaryDateFormat);
  const toString = formatDate(to, addressSummaryDateFormat);
  const errorMessage = `${fromString} - ${toString}.`;

  return { gaps: sortedGaps, isComplete: false, errorMessage };

  function getAnswer(questionId: string, groupNumber: number): HpAnswerValueTypes {
    const controlId = createControlId({ questionId, groupNumber, options: { noPrefix: true } });
    return thisRef.getAnswerValue(controlId);
  }

  function pushToGaps(gap: { from: Date; to: Date }): void {
    gaps.push({ from: toISODateOnly(gap.from), to: toISODateOnly(gap.to) });
  }
}

export function areDatesContiguous(date1: Date, date2: Date): boolean {
  const date1Tmp = new Date(date1);
  const date1PlusOneMonth = dateFns.add(date1Tmp, { months: 1 });
  const isSameYear = date1.getFullYear() === date2.getFullYear();
  const isSameMonth = date1.getMonth() === date2.getMonth();
  const isMonthAfter = date1PlusOneMonth.getMonth() === date2.getMonth();
  const isYearAfter = date2.getFullYear() === date1.getFullYear() + 1;

  return (
    (isSameYear && (isSameMonth || isMonthAfter)) || (isYearAfter && date1.getMonth() === 11 && date2.getMonth() === 0)
  );
}

export function toISODateOnly(date: Date): string {
  return date.toISOString().split('T')[0];
}

export function getDateFiveYearsAgo(): Date {
  const today = new Date();
  today.setFullYear(today.getFullYear() - 5);
  return today;
}

/**
 * Returns true if lastVisit falls within the inspection window (14 days) before lossDate,
 * false if it falls outside, or null if either date is not yet set.
 */
export function calculateInspectionCondition(lossDate: string | null, lastVisit: string | null): boolean | null {
  if (!lossDate || !lastVisit) {
    return null;
  }

  const diffDays = dateFns.differenceInDays(new Date(lossDate), new Date(lastVisit));
  return diffDays >= 0 && diffDays <= 14;
}
